import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { formatApiErrorMessage } from '../api/apiErrorMessage'
import {
  analyticsBandDateRange,
  DEFAULT_BAND_RANGE_DAYS,
  fetchCategoryBands,
  fetchPriceHistory,
  fetchPriceMoves,
  type CategoryBandDto,
  type CategoryBandSourceKind,
  type PriceHistoryPointDto,
  type PriceMoveDirection,
  type PriceMoveDto,
} from '../api/marketAnalytics'
import {
  listRestaurantPrivateSuppliers,
  type RestaurantPrivateSupplierDto,
} from '../api/restaurantMyPrices'
import { listCatalogLists, type CatalogListDto } from '../api/catalogLists'
import { listProductTypes, resolveProductTypes, type ProductTypeDto } from '../api/productTypes'
import { useAuth } from '../auth/AuthProvider'
import { DataTable } from '../components/DataTable'
import { MarketPageHeader } from '../components/MarketPageHeader'
import { PageScrollJumpButtons } from '../components/PageScrollJumpButtons'
import { PriceHistoryChart } from '../components/PriceHistoryChart'
import { ProductTypeCascadePicker } from '../components/ProductTypeCascadePicker'
import { SelectCombobox } from '../components/SelectCombobox'
import { StatTile, StatTileRow } from '../components/StatTile'
import { TableDensityToggle } from '../components/TableDensityToggle'
import { useTableDensity } from '../hooks/useTableDensity'
import {
  aggregateBandsByWeek,
  buildSupplierPriceMoveSummary,
  formatAnalyticsDateTime,
  formatAnalyticsPct,
  formatAnalyticsRub,
  formatAnalyticsWeekLabel,
  filterPriceMovesByName,
  priceMoveItemLabel,
  priceMoveToneClass,
  sortPriceMovesByColumn,
  type PriceMoveSortDir,
  type PriceMoveSortKey,
  type SupplierPriceMoveSummary,
  type WeeklyBand,
} from './marketAnalyticsFormat'

const MOVE_DIRECTION_OPTIONS: Array<{ value: PriceMoveDirection; label: string }> = [
  { value: 'all', label: 'Все' },
  { value: 'down', label: 'Подешевели' },
  { value: 'up', label: 'Подорожали' },
]

const BAND_SOURCE_OPTIONS: Array<{ value: CategoryBandSourceKind; label: string }> = [
  { value: 'list', label: 'Прайс-листы' },
  { value: 'deal', label: 'Заявки' },
]

const MOVE_SORT_COLUMNS: Array<{ key: PriceMoveSortKey; label: string }> = [
  { key: 'item', label: 'Позиция' },
  { key: 'supplier', label: 'Поставщик' },
  { key: 'previous', label: 'Было' },
  { key: 'current', label: 'Стало' },
  { key: 'delta', label: 'Δ' },
  { key: 'when', label: 'Когда' },
]

const MOVE_SORT_SELECT_OPTIONS: Array<{ value: string; label: string }> = MOVE_SORT_COLUMNS.flatMap(
  (column) => [
    { value: `${column.key}:asc`, label: `${column.label} · А–Я / мин→макс` },
    { value: `${column.key}:desc`, label: `${column.label} · Я–А / макс→мин` },
  ],
)

const BandRangeChart = ({ weeks }: { weeks: WeeklyBand[] }) => {
  const scale = useMemo(() => {
    if (weeks.length === 0) {
      return { min: 0, max: 1 }
    }
    const mins = weeks.map((band) => band.min_price)
    const maxs = weeks.map((band) => band.max_price)
    const min = Math.min(...mins)
    const max = Math.max(...maxs)
    if (min === max) {
      return { min: min * 0.9, max: max * 1.1 || 1 }
    }
    const pad = (max - min) * 0.08
    return { min: min - pad, max: max + pad }
  }, [weeks])

  if (weeks.length === 0) {
    return <p className="echat-muted">Нет данных коридора за выбранный период.</p>
  }

  const span = scale.max - scale.min || 1

  return (
    <ul className="market-analytics-bands" aria-label="Ценовой коридор по неделям">
      {weeks.map((band) => {
        const left = ((band.min_price - scale.min) / span) * 100
        const width = ((band.max_price - band.min_price) / span) * 100
        const avgLeft = ((band.avg_price - scale.min) / span) * 100
        return (
          <li
            key={`${band.week_start}-${band.unit}-${band.source_kind}`}
            className="market-analytics-bands__row"
          >
            <span className="market-analytics-bands__day">
              {formatAnalyticsWeekLabel(band.week_start, band.week_end)}
            </span>
            <div className="market-analytics-bands__track" aria-hidden="true">
              <span
                className="market-analytics-bands__range"
                style={{ left: `${left}%`, width: `${Math.max(width, 1.5)}%` }}
              />
              <span className="market-analytics-bands__avg" style={{ left: `${avgLeft}%` }} />
            </div>
            <span className="market-analytics-bands__meta">
              {formatAnalyticsRub(band.min_price)} – {formatAnalyticsRub(band.max_price)}
              <span className="echat-muted"> · ср. {formatAnalyticsRub(band.avg_price)}</span>
            </span>
          </li>
        )
      })}
    </ul>
  )
}

export const MarketAnalyticsScreen = () => {
  const { session } = useAuth()
  const organizationId = session?.organizationId ?? null
  const { density, setDensity, tableClass } = useTableDensity()

  const [moveDirection, setMoveDirection] = useState<PriceMoveDirection>('all')
  const [privateSupplierId, setPrivateSupplierId] = useState<string>('')
  const [catalogListId, setCatalogListId] = useState<number | null>(null)
  const [nameQuery, setNameQuery] = useState('')
  const [sortKey, setSortKey] = useState<PriceMoveSortKey>('delta')
  const [sortDir, setSortDir] = useState<PriceMoveSortDir>('desc')
  const [bandSourceKind, setBandSourceKind] = useState<CategoryBandSourceKind>('list')
  const [bandProductTypeId, setBandProductTypeId] = useState<number | null>(null)
  const defaultBandRange = useMemo(() => analyticsBandDateRange(DEFAULT_BAND_RANGE_DAYS), [])
  const [bandFrom, setBandFrom] = useState(defaultBandRange.from)
  const [bandTo, setBandTo] = useState(defaultBandRange.to)

  const [moves, setMoves] = useState<PriceMoveDto[]>([])
  const [bands, setBands] = useState<CategoryBandDto[]>([])
  const [privateSuppliers, setPrivateSuppliers] = useState<RestaurantPrivateSupplierDto[]>([])
  const [catalogLists, setCatalogLists] = useState<CatalogListDto[]>([])
  const [productTypes, setProductTypes] = useState<ProductTypeDto[]>([])
  const [typeNames, setTypeNames] = useState<Map<number, string>>(new Map())

  const [expandedItemId, setExpandedItemId] = useState<number | null>(null)
  const [historyByItem, setHistoryByItem] = useState<Record<number, PriceHistoryPointDto[]>>({})
  const [historyLoadingId, setHistoryLoadingId] = useState<number | null>(null)
  const [historyErrorByItem, setHistoryErrorByItem] = useState<Record<number, string>>({})

  const [supplierSummary, setSupplierSummary] = useState<SupplierPriceMoveSummary | null>(null)
  const [supplierSummaryLoading, setSupplierSummaryLoading] = useState(false)

  const [loading, setLoading] = useState(true)
  const [movesLoading, setMovesLoading] = useState(false)
  const [bandsLoading, setBandsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [bandsError, setBandsError] = useState<string | null>(null)

  const loadMoves = useCallback(async () => {
    if (organizationId == null) {
      return
    }
    setMovesLoading(true)
    try {
      const priceMoves = await fetchPriceMoves(organizationId, {
        limit: 80,
        direction: moveDirection,
        privateSupplierId: privateSupplierId ? Number(privateSupplierId) : null,
        catalogListId,
      })
      setMoves(priceMoves)
    } catch (err) {
      setError(formatApiErrorMessage(err, 'Не удалось загрузить движения цен'))
      setMoves([])
    } finally {
      setMovesLoading(false)
    }
  }, [
    organizationId,
    moveDirection,
    privateSupplierId,
    catalogListId,
  ])

  const loadCore = useCallback(async () => {
    if (organizationId == null) {
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const [types, privateList, lists] = await Promise.all([
        listProductTypes(),
        listRestaurantPrivateSuppliers(organizationId),
        listCatalogLists(organizationId).catch(() => [] as CatalogListDto[]),
      ])
      setProductTypes(types)
      setPrivateSuppliers(privateList)
      setCatalogLists(lists)
      const nameMap = new Map<number, string>()
      for (const type of types) {
        nameMap.set(type.id, type.name)
      }
      setTypeNames(nameMap)
    } catch (err) {
      setError(formatApiErrorMessage(err, 'Не удалось загрузить аналитику'))
    } finally {
      setLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    void loadCore()
  }, [loadCore])

  useEffect(() => {
    void loadMoves()
  }, [loadMoves])

  // Сбрасываем раскрытый график при смене фильтра — иначе на «Подорожали»
  // остаётся линия от ранее выбранной подешевевшей позиции.
  useEffect(() => {
    setExpandedItemId(null)
  }, [moveDirection, privateSupplierId, catalogListId, nameQuery])

  useEffect(() => {
    if (
      organizationId == null ||
      !privateSupplierId
    ) {
      setSupplierSummary(null)
      setSupplierSummaryLoading(false)
      return
    }
    let cancelled = false
    void (async () => {
      setSupplierSummaryLoading(true)
      try {
        const supplierMoves = await fetchPriceMoves(organizationId, {
          limit: 200,
          direction: 'all',
          privateSupplierId: Number(privateSupplierId),
          catalogListId,
        })
        if (!cancelled) {
          setSupplierSummary(buildSupplierPriceMoveSummary(supplierMoves, 5))
        }
      } catch {
        if (!cancelled) {
          setSupplierSummary(null)
        }
      } finally {
        if (!cancelled) {
          setSupplierSummaryLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [organizationId, privateSupplierId, catalogListId])

  useEffect(() => {
    if (
      organizationId == null ||
      bandProductTypeId == null ||
      !bandFrom ||
      !bandTo
    ) {
      setBands([])
      return
    }
    let cancelled = false
    void (async () => {
      setBandsLoading(true)
      setBandsError(null)
      try {
        const data = await fetchCategoryBands(organizationId, bandProductTypeId, {
          from: bandFrom,
          to: bandTo,
          sourceKind: bandSourceKind,
        })
        if (!cancelled) {
          setBands(data)
        }
      } catch (err) {
        if (!cancelled) {
          setBandsError(formatApiErrorMessage(err, 'Не удалось загрузить коридоры'))
          setBands([])
        }
      } finally {
        if (!cancelled) {
          setBandsLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [
    organizationId,
    bandProductTypeId,
    bandSourceKind,
    bandFrom,
    bandTo,
  ])

  const resolveTypeName = useCallback((id: number | null) => {
    if (id == null) {
      return 'Без категории'
    }
    return typeNames.get(id) ?? `Категория #${id}`
  }, [typeNames])

  const visibleMoves = useMemo(() => {
    const filtered = filterPriceMovesByName(moves, nameQuery, resolveTypeName)
    return sortPriceMovesByColumn(filtered, sortKey, sortDir, resolveTypeName)
  }, [moves, nameQuery, sortKey, sortDir, resolveTypeName])
  const weeklyBands = useMemo(() => aggregateBandsByWeek(bands), [bands])

  const privateSupplierOptions = useMemo(
    () => [
      { value: '', label: 'Все поставщики' },
      ...privateSuppliers.map((supplier) => ({
        value: String(supplier.id),
        label: supplier.legalName,
      })),
    ],
    [privateSuppliers],
  )

  const catalogListOptions = useMemo(
    () => [
      { value: '', label: 'Все позиции' },
      ...catalogLists.map((list) => ({ value: String(list.id), label: list.name })),
    ],
    [catalogLists],
  )
  const favoriteList = catalogLists.find((list) => list.systemKey === 'favorites') ?? null

  const handleSortColumn = (column: PriceMoveSortKey) => {
    if (sortKey === column) {
      setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortKey(column)
    setSortDir('asc')
  }

  const handleToggleExpand = async (catalogItemId: number) => {
    if (expandedItemId === catalogItemId) {
      setExpandedItemId(null)
      return
    }
    setExpandedItemId(catalogItemId)
    if (organizationId == null || historyByItem[catalogItemId]) {
      return
    }
    setHistoryLoadingId(catalogItemId)
    setHistoryErrorByItem((prev) => {
      const next = { ...prev }
      delete next[catalogItemId]
      return next
    })
    try {
      const points = await fetchPriceHistory(organizationId, catalogItemId, 90)
      setHistoryByItem((prev) => ({ ...prev, [catalogItemId]: points }))
    } catch (err) {
      setHistoryErrorByItem((prev) => ({
        ...prev,
        [catalogItemId]: formatApiErrorMessage(err, 'Не удалось загрузить историю цены'),
      }))
    } finally {
      setHistoryLoadingId(null)
    }
  }

  // Warm type names for moves when product types arrive / moves change.
  useEffect(() => {
    const missing = moves
      .map((move) => move.product_type_id)
      .filter((id): id is number => id != null && !typeNames.has(id))
    if (missing.length === 0) {
      return
    }
    let cancelled = false
    void resolveProductTypes(missing).then((resolved) => {
      if (cancelled) {
        return
      }
      setTypeNames((prev) => {
        const next = new Map(prev)
        for (const type of resolved) {
          next.set(type.id, type.name)
        }
        return next
      })
    })
    return () => {
      cancelled = true
    }
  }, [moves, typeNames])


  const selectedSupplierLabel =
    privateSupplierOptions.find((option) => option.value === privateSupplierId)?.label ?? null

  return (
    <section className="market-page market-page--overview market-page--analytics">
      <MarketPageHeader title="Динамика цен" />

      {loading ? <p className="echat-muted">Загрузка…</p> : null}
      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && !error ? (
        <>
          <div className="market-analytics-intro">
            <p className="echat-muted market-analytics-panel__hint">
              Выберите позицию, чтобы посмотреть график изменения цен
            </p>

            <div className="market-analytics-moves__filters">
              <div className="market-pricing__period" role="tablist" aria-label="Направление изменения">
                {MOVE_DIRECTION_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="tab"
                    aria-selected={moveDirection === option.value}
                    className={
                      moveDirection === option.value
                        ? 'market-pricing__period-btn market-pricing__period-btn--active'
                        : 'market-pricing__period-btn'
                    }
                    onClick={() => setMoveDirection(option.value)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <SelectCombobox
                className="market-filters__field market-analytics-moves__supplier"
                label="Поставщик"
                value={privateSupplierId}
                options={privateSupplierOptions}
                onChange={setPrivateSupplierId}
              />
              <SelectCombobox
                className="market-filters__field market-analytics-moves__list"
                label="Мои списки"
                value={catalogListId != null ? String(catalogListId) : ''}
                options={catalogListOptions}
                onChange={(next) => setCatalogListId(next ? Number(next) : null)}
              />
              <label className="echat-field market-analytics-moves__favorites">
                <span className="echat-field__label" aria-hidden="true">&nbsp;</span>
                <span className="market-analytics-moves__favorites-control">
                  <input
                    type="checkbox"
                    checked={favoriteList != null && catalogListId === favoriteList.id}
                    disabled={favoriteList == null}
                    onChange={(event) =>
                      setCatalogListId(event.target.checked ? favoriteList?.id ?? null : null)
                    }
                  />
                  <span>Только избранное</span>
                </span>
              </label>
              <label className="echat-field market-filters__field market-analytics-moves__search">
                <span className="echat-field__label">Поиск</span>
                <input
                  type="search"
                  className="echat-field__input"
                  value={nameQuery}
                  onChange={(event) => setNameQuery(event.target.value)}
                  placeholder="Название позиции"
                  aria-label="Поиск позиции по названию"
                  autoComplete="off"
                />
              </label>
              <label className="echat-field market-filters__field market-analytics-moves__sort-mobile">
                <span className="echat-field__label">Сортировка</span>
                <SelectCombobox
                  label="Сортировка столбцов"
                  hideLabel
                  embedded
                  value={`${sortKey}:${sortDir}`}
                  options={MOVE_SORT_SELECT_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                  onChange={(next) => {
                    const [nextKey, nextDir] = next.split(':')
                    setSortKey(nextKey as PriceMoveSortKey)
                    setSortDir(nextDir as PriceMoveSortDir)
                  }}
                />
              </label>
              <div className="market-analytics-moves__density">
                <span className="echat-field__label">Плотность</span>
                <TableDensityToggle density={density} onChange={setDensity} />
              </div>
            </div>

            {privateSupplierId ? (
              <section className="market-analytics-supplier-block" aria-label="Сводка по поставщику">
                {supplierSummaryLoading ? (
                  <p className="echat-muted">Считаем сводку по прайсу…</p>
                ) : supplierSummary == null || supplierSummary.moveCount === 0 ? (
                  <section className="market-overview-priority" aria-label="Нет изменений">
                    <div className="market-overview-priority__inner">
                      <div className="market-overview-priority__content">
                        <h2 className="market-overview-priority__title">
                          {selectedSupplierLabel ?? 'Поставщик'}
                        </h2>
                        <p className="market-overview-priority__text">
                          Нет изменений в прайсе — история появится после следующих импортов.
                        </p>
                      </div>
                    </div>
                  </section>
                ) : (
                  <>
                    <StatTileRow className="market-my-prices-metrics market-analytics-supplier-metrics">
                      <StatTile
                        label="Среднее изменение"
                        value={
                          <span className={priceMoveToneClass(supplierSummary.avgDeltaPct ?? 0)}>
                            {formatAnalyticsPct(supplierSummary.avgDeltaPct).replace('.', ',')}
                          </span>
                        }
                      />
                      <StatTile label="С изменением" value={supplierSummary.moveCount} />
                      <StatTile label="Подорожали" value={supplierSummary.upCount} />
                      <StatTile label="Подешевели" value={supplierSummary.downCount} />
                    </StatTileRow>

                    <article className="echat-panel market-analytics-tops-panel">
                      <div className="market-catalog-duplicates__header">
                        <div>
                          <h2 className="echat-panel__subtitle">Ключевые движения</h2>
                          <p className="echat-muted">
                            {selectedSupplierLabel
                              ? `${selectedSupplierLabel} · ТОП-5 роста и снижения`
                              : 'ТОП-5 роста и снижения'}
                          </p>
                        </div>
                      </div>
                      <div className="market-analytics-supplier-summary__tops">
                        {(
                          [
                            {
                              key: 'up',
                              title: 'Подорожали',
                              empty: 'Нет подорожаний',
                              moves: supplierSummary.topUp,
                              arrow: '↑',
                            },
                            {
                              key: 'down',
                              title: 'Подешевели',
                              empty: 'Нет снижений',
                              moves: supplierSummary.topDown,
                              arrow: '↓',
                            },
                          ] as const
                        ).map((column) => (
                          <div key={column.key}>
                            <h3 className="market-analytics-supplier-summary__title">{column.title}</h3>
                            {column.moves.length === 0 ? (
                              <p className="echat-muted">{column.empty}</p>
                            ) : (
                              <ol className="market-analytics-supplier-summary__list">
                                {column.moves.map((move) => (
                                  <li
                                    key={`${column.key}-${move.catalog_item_id}`}
                                    className="market-analytics-supplier-summary__item"
                                  >
                                    <span className="market-analytics-supplier-summary__name">
                                      {move.item_name?.trim() || resolveTypeName(move.product_type_id)}
                                    </span>
                                    <span className="market-analytics-supplier-summary__was-became echat-muted">
                                      {formatAnalyticsRub(move.previous_price)} →{' '}
                                      {formatAnalyticsRub(move.current_price)}
                                    </span>
                                    <span className={priceMoveToneClass(move.delta_abs)}>
                                      {column.arrow}{' '}
                                      {formatAnalyticsPct(move.delta_pct).replace('.', ',')}
                                    </span>
                                  </li>
                                ))}
                              </ol>
                            )}
                          </div>
                        ))}
                      </div>
                    </article>
                  </>
                )}
              </section>
            ) : null}
          </div>

          <section className="market-analytics-moves" aria-label="Мои изменения цен">
            {movesLoading ? (
              <p className="echat-muted">Обновляем движения…</p>
            ) : visibleMoves.length === 0 ? (
              <p className="echat-muted">
                {nameQuery.trim()
                  ? `Нет позиций по запросу «${nameQuery.trim()}».`
                  : 'Нет изменений по выбранным фильтрам. История копится после импортов в «Мои прайсы».'}
              </p>
            ) : (
              <DataTable
                wrapClassName="market-table-wrap--cards"
                className={['market-table--cards', tableClass].filter(Boolean).join(' ')}
                caption="Мои изменения цен"
              >
                <thead>
                  <tr>
                    {MOVE_SORT_COLUMNS.map((column) => {
                      const active = sortKey === column.key
                      return (
                        <th
                          key={column.key}
                          aria-sort={
                            active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
                          }
                        >
                          <button
                            type="button"
                            className={
                              active
                                ? 'market-table__sort is-active'
                                : 'market-table__sort'
                            }
                            onClick={() => handleSortColumn(column.key)}
                            aria-label={
                              active
                                ? `${column.label}, сейчас ${sortDir === 'asc' ? 'по возрастанию' : 'по убыванию'}`
                                : `Сортировать по столбцу ${column.label}`
                            }
                          >
                            <span className="market-table__sort-label">{column.label}</span>
                            <span className="market-table__sort-icon" aria-hidden="true">
                              {active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                            </span>
                          </button>
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {visibleMoves.map((move) => {
                    const expanded = expandedItemId === move.catalog_item_id
                    return (
                      <Fragment key={`${move.catalog_item_id}-${move.observed_at}`}>
                        <tr
                          className={
                            expanded
                              ? 'market-analytics-moves__row is-expanded'
                              : 'market-analytics-moves__row'
                          }
                          onClick={() => void handleToggleExpand(move.catalog_item_id)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              void handleToggleExpand(move.catalog_item_id)
                            }
                          }}
                          tabIndex={0}
                          aria-expanded={expanded}
                        >
                          <td data-label="Позиция" className="market-table__cell--primary">
                            <div className="market-table__cell--truncate">
                              {priceMoveItemLabel(move, resolveTypeName)}
                            </div>
                            <div className="echat-muted market-analytics-sku">
                              #{move.catalog_item_id}
                              {move.unit ? ` · ${move.unit}` : ''}
                              {move.product_type_id != null
                                ? ` · ${resolveTypeName(move.product_type_id)}`
                                : ''}
                            </div>
                          </td>
                          <td data-label="Поставщик">{move.supplier_name?.trim() || '—'}</td>
                          <td data-label="Было">{formatAnalyticsRub(move.previous_price)}</td>
                          <td data-label="Стало">{formatAnalyticsRub(move.current_price)}</td>
                          <td data-label="Δ">
                            <span className={priceMoveToneClass(move.delta_abs)}>
                              {move.delta_abs < 0 ? '↓' : '↑'}{' '}
                              {formatAnalyticsPct(move.delta_pct).replace('.', ',')}
                            </span>
                          </td>
                          <td data-label="Когда">{formatAnalyticsDateTime(move.observed_at)}</td>
                        </tr>
                        {expanded ? (
                          <tr className="market-analytics-moves__detail">
                            <td colSpan={6}>
                              <PriceHistoryChart
                                points={historyByItem[move.catalog_item_id] ?? []}
                                loading={historyLoadingId === move.catalog_item_id}
                                error={historyErrorByItem[move.catalog_item_id] ?? null}
                              />
                              <div className="market-analytics-history__actions">
                                <Link
                                  to="/my-prices"
                                  className="echat-btn echat-btn--ghost echat-btn--sm"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  Открыть в «Моих прайсах»
                                </Link>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    )
                  })}
                </tbody>
              </DataTable>
            )}
          </section>

          <details className="echat-panel market-analytics-panel market-analytics-bands-panel">
            <summary className="market-analytics-bands-panel__summary">
              <span>
                <strong>Ценовые коридоры рынка</strong>
                <span className="echat-muted"> · вторичный срез по подкатегории</span>
              </span>
            </summary>
            <div className="market-analytics-bands__filters">
              <ProductTypeCascadePicker
                productTypes={productTypes}
                value={bandProductTypeId}
                onChange={setBandProductTypeId}
                compact
                allowEmpty
                emptyLabel="Выберите подкатегорию"
                showQuickSearch={false}
                showCascadeLevels
                className="market-product-type-cascade market-product-type-cascade--levels-only"
              />
              <div className="market-analytics-bands__controls">
                <label className="echat-field">
                  <span className="echat-field__label">С</span>
                  <input
                    type="date"
                    className="echat-field__input"
                    value={bandFrom}
                    max={bandTo}
                    onChange={(event) => setBandFrom(event.target.value)}
                  />
                </label>
                <label className="echat-field">
                  <span className="echat-field__label">По</span>
                  <input
                    type="date"
                    className="echat-field__input"
                    value={bandTo}
                    min={bandFrom}
                    onChange={(event) => setBandTo(event.target.value)}
                  />
                </label>
                <div className="echat-field market-analytics-bands__source">
                  <span className="echat-field__label">Источник</span>
                  <div className="market-pricing__period" role="tablist" aria-label="Источник коридора">
                    {BAND_SOURCE_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        role="tab"
                        aria-selected={bandSourceKind === option.value}
                        className={
                          bandSourceKind === option.value
                            ? 'market-pricing__period-btn market-pricing__period-btn--active'
                            : 'market-pricing__period-btn'
                        }
                        onClick={() => setBandSourceKind(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="echat-field market-analytics-bands__preset">
                  <span className="echat-field__label" aria-hidden="true">
                    &nbsp;
                  </span>
                  <button
                    type="button"
                    className="echat-btn echat-btn--ghost market-analytics-bands__preset-btn"
                    onClick={() => {
                      const range = analyticsBandDateRange(DEFAULT_BAND_RANGE_DAYS)
                      setBandFrom(range.from)
                      setBandTo(range.to)
                    }}
                  >
                    4 недели
                  </button>
                </div>
              </div>
            </div>
            {bandProductTypeId == null ? (
              <p className="echat-muted">
                Выберите подкатегорию, чтобы увидеть min / max / avg по неделям.
              </p>
            ) : bandsLoading ? (
              <p className="echat-muted">Загрузка коридора…</p>
            ) : bandsError ? (
              <p className="echat-error" role="alert">
                {bandsError}
              </p>
            ) : (
              <BandRangeChart weeks={weeklyBands} />
            )}
          </details>
          {visibleMoves.length > 0 ? <PageScrollJumpButtons /> : null}
        </>
      ) : null}
    </section>
  )
}
